import { FC, useEffect, useMemo, useState } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { convertsCentsToCurrency } from '@npi/buddy-utils';

import { navigate, routes } from '@redwoodjs/router';

import { NPIDataGrid } from 'src/components/NPIDataGrid/NPIDataGrid';
import { createColumnDef } from 'src/components/NPIDataGrid/NPIDataGrid.utils';
import ReusableGrid from 'src/components/ReusableGrid';
import { DEFAULT_BORDER } from 'src/constants';
import { ROLE_PERMISSIONS } from 'src/constants/role-permissions';
import useGeneratePresignedUrl from 'src/hooks/useGeneratePresignedUrl';
import useHasPermission from 'src/hooks/useHasPermission';
import { Principal } from 'src/types/AddMerchant/merchantInfo';
import buildContact from 'src/utils/buildContact';

interface OrderItem {
    imageUrl: string;
    description: string;
    quantity: number;
    monthlyPrice: number;
    terminalId: string;
}

interface DisplayConfig {
    showMerchantAgreement: boolean;
    showAddServiceButton?: boolean;
}

interface ServiceDetailsSectionProps {
    principals: Principal[];
    contractUrl?: string | null;
    merchantId?: string | null;
    orderPrimary?: OrderItem[];
    orderSecondary?: OrderItem[];
    terminalId?: string;
    orderPrimaryUrls?: { imageUrl: string }[];
    orderSecondaryUrls?: { imageUrl: string }[];
    displayConfig?: DisplayConfig;
    openPDFModal?: () => void;
}

const ServiceDetailsSection: FC<ServiceDetailsSectionProps> = ({
    principals,
    contractUrl,
    merchantId,
    terminalId,
    orderPrimary = [],
    orderSecondary = [],
    orderPrimaryUrls = [],
    orderSecondaryUrls = [],
    displayConfig = {
        showMerchantAgreement: true,
        showAddServiceButton: true,
    },
    openPDFModal,
}) => {
    const handleViewAgreement = () => {
        openPDFModal();
    };

    const { validateAwsURL } = useGeneratePresignedUrl();
    const [productImages, setProductImages] = useState<Record<number, string>>({});
    const hasPermission = useHasPermission(ROLE_PERMISSIONS.addService);

    useEffect(() => {
        const fetchPresignedUrls = async () => {
            const urlMap: Record<number, string> = {};
            const all = [...orderPrimaryUrls, ...orderSecondaryUrls];

            for (let i = 0; i < all.length; i++) {
                const url = await validateAwsURL(all[i].imageUrl);
                urlMap[i + 1] = url ?? all[i].imageUrl;
            }

            setProductImages(urlMap);
        };

        fetchPresignedUrls();
    }, [orderPrimaryUrls, orderSecondaryUrls]);

    const rows = useMemo(() => {
        const all = [...orderPrimary, ...orderSecondary];
        return all.map((o, idx) => {
            return {
                id: idx + 1,
                productImg: productImages[idx + 1] ?? '',
                description: o.description,
                qty: o.quantity,
                monthly: `€ ${convertsCentsToCurrency(o.monthlyPrice) ?? '0'}`,
                tid: terminalId ?? '',
            };
        });
    }, [orderPrimary, orderSecondary, productImages]);

    const columns = [
        createColumnDef({
            field: 'productImg',
            headerName: 'Product',
            renderCell: ({ value }) => {
                return <img src={value} alt="product" style={{ width: 40, height: 40 }} />;
            },
        }),
        createColumnDef({ field: 'description', headerName: 'Description' }),
        createColumnDef({ field: 'qty', headerName: 'QTY' }),
        createColumnDef({ field: 'monthly', headerName: 'Monthly Rental' }),
        createColumnDef({ field: 'tid', headerName: 'TID' }),
    ];

    const principalsRows = useMemo(() => {
        return buildContact(principals).map((pr, index) => {
            return {
                id: index + 1,
                name: `${pr.firstName} ${pr.lastName}`,
                email: pr.email,
                mobilePhoneNumber: pr.mobilePhoneNumber,
                type: pr.isBillingContact ? 'Billing Contact' : '',
            };
        });
    }, [principals]);

    const principalsCols = [
        createColumnDef({ field: 'name', headerName: 'Name', flex: 1 }),
        createColumnDef({ field: 'type', headerName: 'Type', flex: 1 }),
        createColumnDef({ field: 'mobilePhoneNumber', headerName: 'Telephone Number', flex: 1 }),
        createColumnDef({ field: 'email', headerName: 'Email', flex: 1 }),
    ];

    const { showMerchantAgreement = true, showAddServiceButton = true } = displayConfig;

    const addRecordButtonConfig =
        Boolean(merchantId) && hasPermission
            ? {
                  addRecordButtonText: 'Add Service',
                  addRecordCallBack: () => {
                      return navigate(routes.AddService({ merchantId }));
                  },
              }
            : undefined;

    return (
        <Box mt={4} data-testid="service-details-section">
            {showMerchantAgreement && (
                <Box
                    sx={{
                        ...DEFAULT_BORDER,
                        height: 116,
                        px: 3,
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: '#FFFFFF',
                        boxShadow: '0px 4px 20px rgba(38,50,56,0.1)',
                    }}
                >
                    <Typography flex={1} fontSize={21} fontWeight={500}>
                        Merchant’s Card Service Agreement
                    </Typography>

                    <Button
                        variant="npi-primary"
                        disabled={!contractUrl}
                        onClick={handleViewAgreement}
                        isElevationDisabled
                    >
                        View Agreement
                    </Button>
                </Box>
            )}

            <Box mt={showMerchantAgreement ? 5 : 0}>
                <ReusableGrid title="Principals" xs={12}>
                    <Box
                        sx={{
                            width: '100%',
                            '& .MuiDataGrid-toolbarContainer': { display: 'none' },
                        }}
                    >
                        <NPIDataGrid
                            rows={principalsRows}
                            columns={principalsCols}
                            getRowId={(row) => {
                                return row.id;
                            }}
                            dataTestId="principals"
                            autoHeight
                            hideFooter
                        />
                    </Box>
                </ReusableGrid>
            </Box>

            <Box mt={showMerchantAgreement ? 5 : 0}>
                <ReusableGrid
                    title="Products / Services"
                    xs={12}
                    addRecordButtonConfig={showAddServiceButton && addRecordButtonConfig}
                >
                    <Box
                        sx={{
                            width: '100%',
                            '& .MuiDataGrid-toolbarContainer': { display: 'none' },
                        }}
                    >
                        <NPIDataGrid
                            rows={rows}
                            columns={columns}
                            getRowId={(row) => {
                                return row.id;
                            }}
                            dataTestId="checkin-services"
                        />
                    </Box>
                </ReusableGrid>
            </Box>
        </Box>
    );
};

export default ServiceDetailsSection;
